/**
 * Reduces one frame of landmarks to the scalar signals the metrics are built
 * from. Kept separate from the metric definitions so that the per-frame cost is
 * paid once, no matter how many metrics read the result.
 */
import * as geo from "./geometry";
import { POSE_LANDMARKS as L } from "../params";

/**
 * @param world A single pose's `worldLandmarks`, already smoothed.
 * @param t Frame timestamp in milliseconds.
 */
export function buildSample(world, t) {
  return {
    t,
    armAngle: {
      left: geo.armAngleFromTrunk(world, "left"),
      right: geo.armAngleFromTrunk(world, "right"),
    },
    // Bounded 0..180, and what ranges are taken over; see armElevation.
    elevation: {
      left: geo.armElevation(world, "left"),
      right: geo.armElevation(world, "right"),
    },
    elbow: {
      left: geo.elbowAngle(world, "left"),
      right: geo.elbowAngle(world, "right"),
    },
    visibility: {
      left: geo.armVisibility(world, "left"),
      right: geo.armVisibility(world, "right"),
    },
    trunkTilt: geo.trunkTilt(world),
    knee: {
      left: geo.kneeAngle(world, "left"),
      right: geo.kneeAngle(world, "right"),
    },
    // Shoulder-hip-knee: 180 lying flat, closing as the body folds. What a
    // V-sit is counted and scored on.
    hip: {
      left: geo.hipAngle(world, "left"),
      right: geo.hipAngle(world, "right"),
    },
    // Metres. Divided by footLength at scoring time, not here, because the
    // scale wanted is the recording's median rather than this frame's — an
    // occluded foot measures as short as 3cm against a 13cm median, and a
    // per-frame ratio would spike for that reason rather than a raised heel.
    heelLift: {
      left: geo.heelLift(world, "left"),
      right: geo.heelLift(world, "right"),
    },
    footLength: {
      left: geo.footLength(world, "left"),
      right: geo.footLength(world, "right"),
    },
    headPitch: {
      left: geo.headPitchFromTrunk(world, "left"),
      right: geo.headPitchFromTrunk(world, "right"),
    },
    // Signed along +x, with the facing sign applied at scoring time: which way
    // the athlete faces is a property of the take, not of one frame.
    aheadOfKneeRaw: {
      left: {
        head: geo.aheadOfKnee(world[L.LEFT_EAR], world, "left", 1),
        chest: geo.aheadOfKnee(geo.chest(world), world, "left", 1),
      },
      right: {
        head: geo.aheadOfKnee(world[L.RIGHT_EAR], world, "right", 1),
        chest: geo.aheadOfKnee(geo.chest(world), world, "right", 1),
      },
    },
    facingOffset: {
      left: geo.facingOffset(world, "left"),
      right: geo.facingOffset(world, "right"),
    },
    legVisibility: {
      left: geo.legVisibility(world, "left"),
      right: geo.legVisibility(world, "right"),
    },
    swingVisibility: {
      left: geo.swingVisibility(world, "left"),
      right: geo.swingVisibility(world, "right"),
    },
    foldVisibility: {
      left: geo.foldVisibility(world, "left"),
      right: geo.foldVisibility(world, "right"),
    },
    // The rest of the handstand's chain. The hip and the knee it already has;
    // what a line upside down adds is the shoulder, and a gate that spans the
    // whole body from the hand to the foot.
    shoulder: {
      left: geo.shoulderAngle(world, "left"),
      right: geo.shoulderAngle(world, "right"),
    },
    ankle: {
      left: geo.ankleAngle(world, "left"),
      right: geo.ankleAngle(world, "right"),
    },
    handstandVisibility: {
      left: geo.handstandVisibility(world, "left"),
      right: geo.handstandVisibility(world, "right"),
    },
    wallLean: {
      left: geo.wallLean(world, "left"),
      right: geo.wallLean(world, "right"),
    },
    // Signed by which side of the arm the ear is on, with the facing left to
    // the caller — the same arrangement as `aheadOfKneeRaw` and for the same
    // reason: which way the athlete faces belongs to the take, not the frame.
    earOffArmLineRaw: {
      left: geo.earOffArmLine(world, "left"),
      right: geo.earOffArmLine(world, "right"),
    },
  };
}

/**
 * Which arm faces the camera, by mean landmark visibility over a span of
 * samples. In a side view the far arm is occluded and its angles are guesses,
 * so side-view metrics follow the near arm rather than averaging the two.
 */
export function nearSide(samples) {
  let left = 0;
  let right = 0;
  for (const s of samples) {
    left += s.visibility.left;
    right += s.visibility.right;
  }
  return left >= right ? "left" : "right";
}
